/**
 * dataSourcesStore.ts
 *
 * Raw read/write for the `dataSources` object store.
 *
 * Mirrors every method from boardContext's datasource state:
 *   dataSources          → getAllDataSources()
 *   refreshNotes         → bulkLoadDataSources()  (hydration, no queue entry)
 *   setDataSource        → addDataSource()         (called by dataSourceMutations)
 *   updateDataSource     → updateDataSourceInDB()  (called by dataSourceMutations)
 */

import { appDB } from "../db/client";
import type { DataSourceRecord } from "../db/schema";
import type { DatabaseSource } from "@/types/board";

// ─── Converters ───────────────────────────────────────────────────

/** Convert a DatabaseSource (from API) into a DataSourceRecord (for IDB). */
export function toDataSourceRecord(
  dataSource: DatabaseSource,
  workspaceId: string,
  isSync: 0 | 1 = 1
): DataSourceRecord {
  return {
    ...dataSource,
    workspaceId,
    isSync,
    localUpdatedAt: Date.now(),
    syncAttempts: 0,
    syncError: null,
  };
}

/** Convert a DataSourceRecord back to a plain DatabaseSource (for existing components). */
export function toDataSource(record: DataSourceRecord): DatabaseSource {
  const { isSync, localUpdatedAt, syncAttempts, syncError, ...dataSource } = record;
  return dataSource as DatabaseSource;
}

// ─── Reads ────────────────────────────────────────────────────────

/** Get all data sources for a workspace. */
export async function getAllDataSources(workspaceId: string): Promise<DataSourceRecord[]> {
  return appDB.dataSources
    .where("workspaceId")
    .equals(workspaceId)
    .toArray();
}

/** Get a single data source by ID. */
export async function getDataSourceFromDB(dataSourceId: string): Promise<DataSourceRecord | undefined> {
  return appDB.dataSources.get(dataSourceId);
}

// ─── Writes ───────────────────────────────────────────────────────

/**
 * Bulk load data sources from API response.
 * Used during initial hydration. Marks all as isSync: 1.
 */
export async function bulkLoadDataSources(
  dataSources: DatabaseSource[],
  workspaceId: string
): Promise<void> {
  const records = dataSources.map((ds) => toDataSourceRecord(ds, workspaceId, 1));
  await appDB.dataSources.bulkPut(records);
}

/**
 * Add or fully replace a data source locally.
 * isSync: 0 — watcher will sync it to MongoDB.
 */
export async function addDataSource(
  dataSource: DatabaseSource,
  workspaceId: string
): Promise<void> {
  await appDB.dataSources.put(toDataSourceRecord(dataSource, workspaceId, 0));
}

/**
 * Update specific fields on an existing data source.
 * Marks isSync: 0 | 1 = 0 so watcher picks it up.
 */
export async function updateDataSourceInDB(
  dataSourceId: string,
  updates: Partial<DatabaseSource>
): Promise<void> {
  const existing = await appDB.dataSources.get(dataSourceId);
  if (!existing) return;
  await appDB.dataSources.put({
    ...existing,
    ...updates,
    _id: dataSourceId,
    isSync: 0 as 0,
    localUpdatedAt: Date.now(),
  });
}

/**
 * Patch a single property inside a data source's properties map.
 * Safe for stale-closure situations — reads current record fresh from IDB.
 */
export async function patchDataSourcePropertyInDB(
  dataSourceId: string,
  propertyId: string,
  patch: Partial<Record<string, any>>
): Promise<void> {
  const existing = await appDB.dataSources.get(dataSourceId);
  if (!existing?.properties?.[propertyId]) return;
  await appDB.dataSources.put({
    ...existing,
    properties: {
      ...existing.properties,
      [propertyId]: { ...existing.properties[propertyId], ...patch },
    },
    isSync: 0 as 0,
    localUpdatedAt: Date.now(),
  });
}

/**
 * Mark a data source as synced after watcher confirms server write.
 */
export async function markDataSourceSynced(dataSourceId: string): Promise<void> {
  await appDB.dataSources.update(dataSourceId, {
    isSync: 1 as 1,
    syncAttempts: 0,
    syncError: null,
  });
}

/**
 * Record a sync failure on a data source.
 */
export async function recordDataSourceSyncFailure(
  dataSourceId: string,
  error: string
): Promise<void> {
  const existing = await appDB.dataSources.get(dataSourceId);
  if (!existing) return;
  await appDB.dataSources.update(dataSourceId, {
    syncAttempts: (existing.syncAttempts ?? 0) + 1,
    syncError: error,
  });
}

// ─── Unsynced query (used by watcher) ────────────────────────────

/** Get all data sources with pending local changes. */
export async function getUnsyncedDataSources(workspaceId: string): Promise<DataSourceRecord[]> {
  return appDB.dataSources
    .where("workspaceId")
    .equals(workspaceId)
    .and((ds) => ds.isSync === 0)
    .toArray();
}
